import { Suspense, useContext, useEffect, useRef } from "react";
import { useOutlet, useSearchParams, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { PageLoading } from "./standard-loading";

import ReactGA from 'react-ga4';
import { AuthContext } from "../contexts/auth-context";
import { AuthModal } from "./auth";
import { GlobalContext } from "../contexts/global-context";
import { MainNavBar } from "./main-navbar";
import { Footer } from "./footer";
import { Box, Container } from "@mui/material";
import { HeroOverlay } from "./hero-overlay";
import { CookieConsentBanner } from "./cookie-consent-banner";
import { OnboardingHints } from "./onboarding-hints";

export function Root() {
    const {
        showAuthModal,
        setShowAuthModal,
        authModalType, 
        setAuthModalType, 
        setLoginMessage,
        isMobile
    } = useContext(GlobalContext);
    
    const { authRedirectPath, setAuthRedirectPath, user } = useContext(AuthContext);
    
    const navigate = useNavigate();
    const location = useLocation();
    const isLanding = location.pathname === "/";
    const outlet = useOutlet();

    // Use a stable key for /designs/:id routes so navigating slug → uuid
    // (e.g. after a build completes) doesn't unmount/remount the component tree.
    // For all other routes, use the full pathname for page transition animations.
    const routeKey = location.pathname.startsWith('/designs/')
        ? '/designs/:id'
        : location.pathname;
    const [uriParams] = useSearchParams();
    const processedPayments = useRef(new Set());

    // Track page views on route change
    useEffect(() => {
        ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    }, [location]);

    // Handle password reset with key parameter
    useEffect(() => {
        if (uriParams.get("password-reset") !== null && uriParams.get("key")) {
            setAuthModalType("password-reset");
            setShowAuthModal(true);
        }
    }, [setAuthModalType, setShowAuthModal, uriParams]);

    // Handle Stripe payment redirects
    useEffect(() => {
        // Check both regular search params and hash-based params
        let paymentIntent = uriParams.get("payment_intent");
        let redirectStatus = uriParams.get("redirect_status");
        
        // If not found in regular search params, check if they're in the hash
        if (!paymentIntent && window.location.search) {
            const searchParams = new URLSearchParams(window.location.search);
            paymentIntent = searchParams.get("payment_intent");
            redirectStatus = searchParams.get("redirect_status");
        }
        
        if (paymentIntent && redirectStatus === "succeeded" && !processedPayments.current.has(paymentIntent)) {
            // Mark this payment as processed to prevent loops
            processedPayments.current.add(paymentIntent);
            
            // Clear query parameters from the URL
            const cleanUrl = `${window.location.origin}${window.location.pathname}`;
            window.history.replaceState(null, '', cleanUrl);
            
            // Navigate to plans with hash routing
            navigate("/library?tab=plan", { replace: true });
        }
    }, [uriParams, navigate]);

    const handleHideAuthModal = () => {
        setShowAuthModal(false);
        setLoginMessage('');

        // Only redirect if user cancelled login while on a protected route
        if (!user && authRedirectPath) {
            navigate("/");
            setAuthRedirectPath(null);
        }
      };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                position: "relative",
            }}
        >
        <Box component="main">
            <MainNavBar transparent={isLanding} hideLogo={isLanding && !isMobile} lightContent={isLanding} />
            <AnimatePresence mode="wait" initial={false}
                onExitComplete={() => window.scrollTo(0, 0)}
            >
                <motion.div
                    key={routeKey}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    style={{ flex: 1 }}
                >
                    <Suspense fallback={<PageLoading />}>
                    {isLanding ? (
                        <>
                            <Container maxWidth="xl" disableGutters>
                                <Box sx={{ mb: 1 }}>
                                    <HeroOverlay />
                                </Box>
                            </Container>
                            {outlet}
                        </>
                    ) : (
                        <Box sx={{
                            maxWidth: '1920px',
                            mx: 'auto',
                            width: '100%'
                        }}>
                            {outlet}
                        </Box>
                    )}
                    </Suspense>
                </motion.div>
            </AnimatePresence>
        </Box>
        <OnboardingHints />
        <AuthModal show={showAuthModal}
            type={authModalType}
            onHide={handleHideAuthModal}
        />
        <Footer />
        <CookieConsentBanner />
        </Box>
    );
}